import { EmployerContactSubmissionData } from '../../../models/submissions/employer-submissions/submission-bodies/update-contacts/employer-contact-submission-data';
import { EmployerContactSubmissionChangeTypeEnum } from '../../shared/components/employer-update-contact-submission-contact-changes/employer-contact-submission-change-type.enum';
import { EmployerContactSubmissionChange } from '../../shared/components/employer-update-contact-submission-contact-changes/employer-contact-submission-change';
import { EmployerContactSubmissionDetailsChange } from '../../shared/components/employer-update-contact-submission-contact-details-changes/employer-contact-submission-details-change';
import { SubmissionInfoItem } from '../../../models/shared/submission-info-item';
import { PhoneNumberUtils } from '../../../utils/phone-number-utils';

export class EmployerUpdateContactSubmissionUtils {
  private static contactsSortFn(
    a: EmployerContactSubmissionChange | EmployerContactSubmissionDetailsChange,
    b: EmployerContactSubmissionChange | EmployerContactSubmissionDetailsChange
  ): number {
    // Sort by primary contact first, then by contact name alphabetically
    if (a.primaryContact !== b.primaryContact) {
      return a.primaryContact ? -1 : 1;
    }

    const nameA = a.contactName.toLowerCase();
    const nameB = b.contactName.toLowerCase();

    return nameA.localeCompare(nameB);
  }

  public static getEmployerContactChangeCardData(
    source: EmployerContactSubmissionData[],
    target: EmployerContactSubmissionData[],
    sourceCardType: EmployerContactSubmissionChangeTypeEnum
  ): EmployerContactSubmissionChange[] {
    return source
      ?.map(contact => {
        const match = target.find(
          c => c.employerContactId !== null && c.employerContactId === contact.employerContactId
        );
        const changeType = !match ? sourceCardType : EmployerContactSubmissionChangeTypeEnum.Unchanged;
        return {
          contactName: contact.firstName + ' ' + contact.lastName,
          changeType,
          primaryContact: contact.primaryContact
        };
      })
      .sort(this.contactsSortFn);
  }

  private static getRelevantKeys(contact: EmployerContactSubmissionData): string[] {
    // Properties to compare
    const keys = [
      'hasSigningAuthority',
      'jobTitle',
      'contactTopic',
      'email',
      'phoneNumber',
      'address.country',
      'address.addressLine1',
      'address.addressLine2',
      'address.addressLine3',
      'address.city',
      'address.state',
      'address.postalCode'
    ];

    if (contact.primaryContact) {
      return keys.filter(k => k !== 'contactTopic');
    } else {
      // Ignore address if contact is not primary and does not have an address.
      return !contact.address ? keys.filter(k => !k.startsWith('address.')) : keys;
    }
  }

  /*
   * Compares two arrays of contacts and marks them as changed if any of the specified fields are different.
   * @param prevContacts - An array of previous contact objects to compare against.
   * @param newContacts - An array of new contact objects to compare.
   * @returns - A formatted array of EmployerContactSubmissionDetailsChanges indicating the changes for each contact.
   */
  public static getEmployerContactDetailsChanges(
    prevContacts: EmployerContactSubmissionData[],
    newContacts: EmployerContactSubmissionData[]
  ): EmployerContactSubmissionDetailsChange[] {
    const result = [];

    for (const newContact of newContacts) {
      const match = prevContacts.find(
        prev =>
          prev.employerContactId !== null &&
          newContact.employerContactId !== null &&
          prev.employerContactId === newContact.employerContactId
      );

      const previousChanges: SubmissionInfoItem[] = [];
      const newChanges: SubmissionInfoItem[] = [];

      for (const k of this.getRelevantKeys(newContact)) {
        const key = k as keyof EmployerContactSubmissionData;
        let hasChanges = false;
        let prevVal: any = '';
        let newVal: any = '';
        let displayPrev: string = '';
        let displayNew: string = '';

        if (match) {
          // Special handling for nested objects and specific formatting
          // i.e: We want to compare state.id but display state.name
          switch (k) {
            case 'address.country':
              prevVal = match.address?.country?.id;
              newVal = newContact.address?.country?.id;
              displayPrev = match.address?.country?.name ?? '';
              displayNew = newContact.address?.country?.name ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.addressLine1':
              prevVal = match.address?.addressLine1;
              newVal = newContact.address?.addressLine1;
              displayPrev = match.address?.addressLine1 ?? '';
              displayNew = newContact.address?.addressLine1 ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.addressLine2':
              prevVal = match.address?.addressLine2;
              newVal = newContact.address?.addressLine2;
              displayPrev = match.address?.addressLine2 ?? '';
              displayNew = newContact.address?.addressLine2 ?? '';
              hasChanges = (prevVal ?? '') !== (newVal ?? '');
              break;

            case 'address.addressLine3':
              prevVal = match.address?.addressLine3;
              newVal = newContact.address?.addressLine3;
              displayPrev = match.address?.addressLine3 ?? '';
              displayNew = newContact.address?.addressLine3 ?? '';
              hasChanges = (prevVal ?? '') !== (newVal ?? '');
              break;

            case 'address.city':
              prevVal = match.address?.city;
              newVal = newContact.address?.city;
              displayPrev = match.address?.city ?? '';
              displayNew = newContact.address?.city ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.state':
              prevVal = match.address?.state?.id;
              newVal = newContact.address?.state?.id;
              displayPrev = match.address?.state?.name ?? '';
              displayNew = newContact.address?.state?.name ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.postalCode':
              prevVal = match.address?.postalCode;
              newVal = newContact.address?.postalCode;
              displayPrev = match.address?.postalCode ?? '';
              displayNew = newContact.address?.postalCode ?? '';
              hasChanges = prevVal !== newVal;
              break;

            default:
              prevVal = match[key];
              newVal = newContact[key];
              hasChanges = prevVal !== newVal;
              if (k === 'phoneNumber') {
                displayPrev = PhoneNumberUtils.formatNumberWithLibPhoneNumber(prevVal, match.phoneNumberCountryCode);
                displayNew = PhoneNumberUtils.formatNumberWithLibPhoneNumber(newVal, newContact.phoneNumberCountryCode);
              } else {
                displayPrev = prevVal ?? '';
                displayNew = newVal ?? '';
              }
              break;
          }

          previousChanges.push({ key: k, value: displayPrev, label: displayPrev, hasChanged: hasChanges });
          newChanges.push({ key: k, value: displayNew, label: displayNew, hasChanged: hasChanges });
        } else {
          // No previous data: treat all as new with hasChanges = true
          switch (k) {
            case 'address.country':
              newVal = newContact.address?.country?.name ?? '';
              break;
            case 'address.addressLine1':
              newVal = newContact.address?.addressLine1 ?? '';
              break;
            case 'address.addressLine2':
              newVal = newContact.address?.addressLine2 ?? '';
              break;
            case 'address.addressLine3':
              newVal = newContact.address?.addressLine3 ?? '';
              break;
            case 'address.city':
              newVal = newContact.address?.city ?? '';
              break;
            case 'address.state':
              newVal = newContact.address?.state?.name ?? '';
              break;
            case 'address.postalCode':
              newVal = newContact.address?.postalCode ?? '';
              break;
            case 'phoneNumber':
              newVal = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                newContact.phoneNumber,
                newContact.phoneNumberCountryCode
              );
              break;
            default:
              newVal = newContact[key] ?? '';
              break;
          }

          newChanges.push({ key: k, value: newVal, label: newVal, hasChanged: true });
        }
      }
      result.push(new EmployerContactSubmissionDetailsChange(newContact, previousChanges, newChanges));
    }
    return result.sort(this.contactsSortFn);
  }

  /**
   * Formats a SubmissionInfoItem for display, including localization and specific formatting.
   * @param change - The SubmissionInfoItem object to format.
   * @return A formatted SubmissionInfoItem with localized key and formatted value.
   */
  public static formatContactDetailsChanges(change: SubmissionInfoItem): SubmissionInfoItem {
    let key = '';
    const value = change.value;

    switch (change.key) {
      case 'jobTitle':
        key = $localize`Job Title`;
        break;
      case 'contactTopic':
        key = $localize`Contact Topic`;
        break;
      case 'email':
        key = $localize`Work Email Address`;
        break;
      case 'phoneNumber':
        key = $localize`Phone Number`;
        break;
      case 'address.country':
        key = $localize`Country`;
        break;
      case 'address.addressLine1':
        key = $localize`Address Line 1`;
        break;
      case 'address.addressLine2':
        key = $localize`Address Line 2`;
        break;
      case 'address.addressLine3':
        key = $localize`Address Line 3`;
        break;
      case 'address.city':
        key = $localize`City/Town`;
        break;
      case 'address.state':
        key = $localize`Province/State/Region`;
        break;
      case 'address.postalCode':
        key = $localize`Postal Code/ZIP`;
        break;
    }

    return { key, value, label: key, hasChanged: change.hasChanged };
  }
}
